import React, { useState, useRef } from 'react';
import { FiUpload, FiCheckCircle, FiXCircle, FiAlertCircle, FiPhone, FiLoader, FiArrowRight, FiRefreshCw, FiServer, FiSend, FiDatabase, FiCheck, FiFile, FiX, FiEdit3, FiFileText } from 'react-icons/fi';
import Calling from '../../api/Calling';

const MobileAdd = () => {
    const [importType, setImportType] = useState('manual'); // 'manual' or 'exal'
    const [mobiles, setMobiles] = useState("");
    const [exalFile, setExalFile] = useState(null);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1); // 1: Input, 2: Processing, 3: Result
    const [fileError, setFileError] = useState("");
    const fileInputRef = useRef(null);

    // Live processing stages
    const [processingStage, setProcessingStage] = useState(0);
    const [stageStatus, setStageStatus] = useState({
        connecting: 'pending',
        sending: 'pending',
        processing: 'pending',
        completing: 'pending'
    });

    const processingStages = [
        { key: 'connecting', icon: FiServer, label: 'Connecting to server', subLabel: 'Establishing secure connection...' },
        { key: 'sending', icon: FiSend, label: 'Sending data', subLabel: importType === 'exal' ? 'Uploading Excel file...' : 'Uploading mobile numbers...' },
        { key: 'processing', icon: FiDatabase, label: 'Processing numbers', subLabel: 'Validating and importing...' },
        { key: 'completing', icon: FiCheck, label: 'Completing', subLabel: 'Finalizing import...' }
    ];

    const ALLOWED_TYPES = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFileError("");
        setExalFile(null);

        if (!file) return;

        // Validate file type
        if (!ALLOWED_TYPES.includes(file.type)) {
            setFileError("Invalid file type. Only Excel (.xlsx, .xls) files are allowed.");
            return;
        }

        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
            setFileError("File size exceeds 10MB limit.");
            return;
        }

        setExalFile(file);
    };

    const handleRemoveFile = () => {
        setExalFile(null);
        setFileError("");
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation based on import type
        if (importType === 'manual') {
            const mobileArray = mobiles
                .split(/\s|,|\n/)
                .map(num => num.trim())
                .filter(num => num);

            if (mobileArray.length === 0) {
                setResult({ status: false, message: 'Enter at least one valid mobile number.' });
                return;
            }
        } else {
            if (!exalFile) {
                setResult({ status: false, message: 'Please select an Excel file to upload.' });
                return;
            }
        }

        // Reset states
        setStep(2);
        setLoading(true);
        setResult(null);
        setProcessingStage(0);
        setStageStatus({
            connecting: 'pending',
            sending: 'pending',
            processing: 'pending',
            completing: 'pending'
        });

        // Stage 1: Connecting
        setStageStatus(prev => ({ ...prev, connecting: 'loading' }));
        await new Promise(resolve => setTimeout(resolve, 800));
        setStageStatus(prev => ({ ...prev, connecting: 'success' }));

        // Stage 2: Sending
        setProcessingStage(1);
        setStageStatus(prev => ({ ...prev, sending: 'loading' }));
        await new Promise(resolve => setTimeout(resolve, 600));
        setStageStatus(prev => ({ ...prev, sending: 'success' }));

        // Stage 3: Processing - Actual API Call
        setProcessingStage(2);
        setStageStatus(prev => ({ ...prev, processing: 'loading' }));

        try {
            let resp;

            if (importType === 'exal') {
                // Excel Import
                const formData = new FormData();
                formData.append('exal', exalFile);
                resp = await Calling.mobileImportExal(formData);
            } else {
                // Manual Import
                const mobileArray = mobiles
                    .split(/\s|,|\n/)
                    .map(num => num.trim())
                    .filter(num => num);
                resp = await Calling.mobileImport({ mobile: mobileArray });
            }

            setStageStatus(prev => ({ ...prev, processing: 'success' }));

            // Stage 4: Completing
            setProcessingStage(3);
            setStageStatus(prev => ({ ...prev, completing: 'loading' }));
            await new Promise(resolve => setTimeout(resolve, 500));
            setStageStatus(prev => ({ ...prev, completing: 'success' }));

            await new Promise(resolve => setTimeout(resolve, 300));

            setResult(resp);
            setStep(3);
        } catch (error) {
            setStageStatus(prev => ({ ...prev, processing: 'error' }));
            setResult({ status: false, message: 'Connection failed. Please try again.' });
            await new Promise(resolve => setTimeout(resolve, 1000));
            setStep(3);
        }

        setLoading(false);
    };

    const handleReset = () => {
        setMobiles("");
        setExalFile(null);
        setFileError("");
        setResult(null);
        setStep(1);
        setProcessingStage(0);
        setStageStatus({
            connecting: 'pending',
            sending: 'pending',
            processing: 'pending',
            completing: 'pending'
        });
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const getPreviewNumbers = () => {
        return mobiles
            .split(/\s|,|\n/)
            .map(num => num.trim())
            .filter(num => num);
    };

    const getStageIcon = (status, Icon) => {
        if (status === 'loading') {
            return <FiLoader className="animate-spin text-main-blue" />;
        } else if (status === 'success') {
            return <FiCheckCircle className="text-green-500" />;
        } else if (status === 'error') {
            return <FiXCircle className="text-red-500" />;
        }
        return <Icon className="text-gray-400" />;
    };

    const isSubmitDisabled = () => {
        if (importType === 'manual') {
            return !mobiles.trim();
        }
        return !exalFile;
    };

    return (
        <div className="w-full text-main-font">
            <div className="bg-main-white shadow-md rounded-xl p-6 md:p-8 w-full border border-main-border">

                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-main-blue/10 rounded-xl flex items-center justify-center">
                        <FiPhone className="text-main-blue text-xl" />
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold text-main-font">Import Mobile Numbers</h2>
                        <p className="text-sm text-gray-500">Add multiple numbers at once</p>
                    </div>
                </div>

                {/* Progress Steps */}
                <div className="flex items-center justify-center gap-2 mb-8">
                    {[
                        { num: 1, label: 'Enter Numbers' },
                        { num: 2, label: 'Processing' },
                        { num: 3, label: 'Complete' }
                    ].map((s, idx) => (
                        <React.Fragment key={s.num}>
                            <div className="flex flex-col items-center">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium text-sm transition-all duration-300
                                    ${step >= s.num
                                        ? 'bg-main-blue text-white'
                                        : 'bg-gray-100 text-gray-400'
                                    } ${step === s.num ? 'ring-4 ring-main-blue/20' : ''}`}
                                >
                                    {step > s.num ? <FiCheckCircle /> : s.num}
                                </div>
                                <span className={`text-xs mt-1.5 hidden md:block ${step >= s.num ? 'text-main-blue font-medium' : 'text-gray-400'}`}>
                                    {s.label}
                                </span>
                            </div>
                            {idx < 2 && (
                                <div className={`w-12 md:w-20 h-1 rounded-full mb-5 transition-all duration-300 ${step > s.num ? 'bg-main-blue' : 'bg-gray-200'}`} />
                            )}
                        </React.Fragment>
                    ))}
                </div>

                {/* Step 1: Input Form */}
                {step === 1 && (
                    <div className="animate-fadeIn">
                        {/* Error Message */}
                        {result && !result.status && (
                            <div className="flex items-center gap-3 p-4 mb-4 rounded-xl bg-red-50 border border-red-200">
                                <FiAlertCircle className="text-red-500 text-xl flex-shrink-0" />
                                <p className="text-red-600 text-sm">{result.message}</p>
                            </div>
                        )}

                        {/* Import Type Selector */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-main-font mb-3">Select Import Type</label>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setImportType('manual');
                                        setFileError("");
                                        setExalFile(null);
                                    }}
                                    className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all duration-300
                                        ${importType === 'manual'
                                            ? 'border-main-blue bg-main-blue/5 shadow-sm'
                                            : 'border-main-border bg-white hover:border-gray-300'
                                        }`}
                                >
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300
                                        ${importType === 'manual' ? 'bg-main-blue/10' : 'bg-gray-100'}`}>
                                        <FiEdit3 className={`text-lg ${importType === 'manual' ? 'text-main-blue' : 'text-gray-500'}`} />
                                    </div>
                                    <div className="text-left">
                                        <div className={`font-medium text-sm ${importType === 'manual' ? 'text-main-blue' : 'text-main-font'}`}>
                                            Manual Entry
                                        </div>
                                        <div className="text-xs text-gray-500">Type or paste numbers</div>
                                    </div>
                                    {importType === 'manual' && (
                                        <FiCheckCircle className="text-main-blue ml-auto" />
                                    )}
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setImportType('exal');
                                        setMobiles("");
                                    }}
                                    className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all duration-300
                                        ${importType === 'exal'
                                            ? 'border-main-blue bg-main-blue/5 shadow-sm'
                                            : 'border-main-border bg-white hover:border-gray-300'
                                        }`}
                                >
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300
                                        ${importType === 'exal' ? 'bg-main-blue/10' : 'bg-gray-100'}`}>
                                        <FiFileText className={`text-lg ${importType === 'exal' ? 'text-main-blue' : 'text-gray-500'}`} />
                                    </div>
                                    <div className="text-left">
                                        <div className={`font-medium text-sm ${importType === 'exal' ? 'text-main-blue' : 'text-main-font'}`}>
                                            Excel Upload
                                        </div>
                                        <div className="text-xs text-gray-500">Upload Excel file</div>
                                    </div>
                                    {importType === 'exal' && (
                                        <FiCheckCircle className="text-main-blue ml-auto" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit}>
                            {/* Manual Input Area */}
                            {importType === 'manual' && (
                                <div className="border-2 border-dashed border-main-border hover:border-main-blue/50 rounded-xl p-6 transition-all duration-300 bg-gray-50/50">
                                    <div className="flex flex-col items-center justify-center mb-4">
                                        <div className="w-14 h-14 bg-main-blue/10 rounded-full flex items-center justify-center mb-3">
                                            <FiUpload className="text-main-blue text-2xl" />
                                        </div>
                                        <p className="font-medium text-main-font">Enter Mobile Numbers</p>
                                        <p className="text-sm text-gray-500 mt-1">Separate by comma, space, or new line</p>
                                    </div>

                                    <textarea
                                        rows="5"
                                        value={mobiles}
                                        onChange={(e) => setMobiles(e.target.value)}
                                        className="w-full border border-main-border rounded-lg p-3 bg-white
                                                   focus:outline-none focus:ring-2 focus:ring-main-blue/50 focus:border-main-blue
                                                   placeholder-gray-400 text-sm resize-none"
                                        placeholder={`Example:\n7879887997\n9876543210, 8989898989`}
                                    />
                                </div>
                            )}

                            {/* Excel Upload Area */}
                            {importType === 'exal' && (
                                <div className="border-2 border-dashed border-main-border hover:border-main-blue/50 rounded-xl p-6 transition-all duration-300 bg-gray-50/50">
                                    {!exalFile ? (
                                        <>
                                            <div className="flex flex-col items-center justify-center">
                                                <div className="w-14 h-14 bg-main-blue/10 rounded-full flex items-center justify-center mb-3">
                                                    <FiFileText className="text-main-blue text-2xl" />
                                                </div>
                                                <p className="font-medium text-main-font">Upload Excel File</p>
                                                <p className="text-sm text-gray-500 mt-1">Max file size: 10MB</p>
                                                <p className="text-xs text-gray-400 mt-1">Supported formats: .xlsx, .xls</p>
                                            </div>

                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                                onChange={handleFileChange}
                                                className="hidden"
                                                id="exal-upload"
                                            />

                                            <label
                                                htmlFor="exal-upload"
                                                className="mt-4 flex items-center justify-center gap-2 w-full py-3 px-4 rounded-lg border border-main-blue text-main-blue font-medium text-sm cursor-pointer hover:bg-main-blue/5 transition-all duration-300"
                                            >
                                                <FiUpload />
                                                Choose File
                                            </label>

                                            {/* File Error */}
                                            {fileError && (
                                                <div className="flex items-center gap-2 mt-3 p-3 rounded-lg bg-red-50 border border-red-200">
                                                    <FiAlertCircle className="text-red-500 flex-shrink-0" />
                                                    <p className="text-red-600 text-sm">{fileError}</p>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        /* File Selected View */
                                        <div className="flex items-center gap-4 p-4 bg-white rounded-lg border border-green-200">
                                            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <FiFile className="text-green-600 text-xl" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-main-font text-sm truncate">{exalFile.name}</p>
                                                <p className="text-xs text-gray-500 mt-0.5">{formatFileSize(exalFile.size)}</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-100 text-green-600 text-xs font-medium">
                                                    <FiCheckCircle className="text-xs" />
                                                    Ready
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={handleRemoveFile}
                                                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all duration-300"
                                                >
                                                    <FiX />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isSubmitDisabled()}
                                className={`w-full mt-6 py-3 rounded-xl font-medium text-white transition-all duration-300 flex items-center justify-center gap-2
                                    ${isSubmitDisabled()
                                        ? 'bg-gray-300 cursor-not-allowed'
                                        : 'bg-main-blue hover:bg-main-blue/90 hover:shadow-lg hover:shadow-main-blue/25'
                                    }`}
                            >
                                Start Import
                                <FiArrowRight />
                            </button>

                            {/* Preview Count - Manual */}
                            {importType === 'manual' && mobiles.trim() && (
                                <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <FiPhone className="text-main-blue" />
                                            <span className="text-sm font-medium text-main-font">Numbers to import</span>
                                        </div>
                                        <span className="bg-main-blue text-white text-sm font-medium px-3 py-1 rounded-full">
                                            {getPreviewNumbers().length}
                                        </span>
                                    </div>
                                    {getPreviewNumbers().length > 0 && getPreviewNumbers().length <= 10 && (
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            {getPreviewNumbers().map((num, idx) => (
                                                <span key={idx} className="bg-white px-3 py-1 rounded-lg text-xs font-mono border border-blue-200">
                                                    {num}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Preview - Excel */}
                            {importType === 'exal' && exalFile && (
                                <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <FiFileText className="text-main-blue" />
                                            <span className="text-sm font-medium text-main-font">File ready to import</span>
                                        </div>
                                        <span className="bg-main-blue text-white text-sm font-medium px-3 py-1 rounded-full">
                                            {formatFileSize(exalFile.size)}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </form>
                    </div>
                )}

                {/* Step 2: Live Processing */}
                {step === 2 && loading && (
                    <div className="animate-fadeIn py-6">
                        {/* Processing Header */}
                        <div className="text-center mb-8">
                            <div className="relative inline-block">
                                <div className="w-16 h-16 border-4 border-main-blue/20 rounded-full"></div>
                                <div className="w-16 h-16 border-4 border-main-blue border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
                                <FiServer className="text-main-blue text-xl absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                            </div>
                            <h3 className="text-lg font-semibold text-main-font mt-4">Processing Import</h3>
                            <p className="text-gray-500 text-sm mt-1">
                                {importType === 'exal'
                                    ? `Importing from ${exalFile?.name}...`
                                    : `Importing ${getPreviewNumbers().length} mobile numbers...`
                                }
                            </p>
                        </div>

                        {/* Live Stages */}
                        <div className="bg-gray-50 rounded-xl p-5 border border-main-border">
                            <div className="space-y-1">
                                {processingStages.map((stage, idx) => {
                                    const status = stageStatus[stage.key];
                                    const isActive = status === 'loading';
                                    const isComplete = status === 'success';
                                    const isError = status === 'error';
                                    const isPending = status === 'pending';

                                    return (
                                        <div
                                            key={stage.key}
                                            className={`flex items-center gap-4 p-3 rounded-lg transition-all duration-300
                                                ${isActive ? 'bg-main-blue/5 border border-main-blue/20' : ''}
                                                ${isComplete ? 'bg-green-50/50' : ''}
                                                ${isError ? 'bg-red-50' : ''}
                                            `}
                                        >
                                            {/* Stage Icon */}
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300
                                                ${isActive ? 'bg-main-blue/10' : ''}
                                                ${isComplete ? 'bg-green-100' : ''}
                                                ${isError ? 'bg-red-100' : ''}
                                                ${isPending ? 'bg-gray-100' : ''}
                                            `}>
                                                {getStageIcon(status, stage.icon)}
                                            </div>

                                            {/* Stage Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className={`font-medium text-sm transition-all duration-300
                                                    ${isActive ? 'text-main-blue' : ''}
                                                    ${isComplete ? 'text-green-600' : ''}
                                                    ${isError ? 'text-red-600' : ''}
                                                    ${isPending ? 'text-gray-400' : ''}
                                                `}>
                                                    {stage.label}
                                                </div>
                                                <div className={`text-xs mt-0.5 transition-all duration-300
                                                    ${isActive ? 'text-main-blue/70' : ''}
                                                    ${isComplete ? 'text-green-500' : ''}
                                                    ${isError ? 'text-red-500' : ''}
                                                    ${isPending ? 'text-gray-400' : ''}
                                                `}>
                                                    {isComplete ? 'Completed' : isError ? 'Failed' : stage.subLabel}
                                                </div>
                                            </div>

                                            {/* Status Badge */}
                                            <div className="flex-shrink-0">
                                                {isActive && (
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-main-blue/10 text-main-blue text-xs font-medium">
                                                        <span className="w-1.5 h-1.5 bg-main-blue rounded-full animate-pulse"></span>
                                                        In Progress
                                                    </span>
                                                )}
                                                {isComplete && (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-100 text-green-600 text-xs font-medium">
                                                        <FiCheck className="text-xs" />
                                                        Done
                                                    </span>
                                                )}
                                                {isError && (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-100 text-red-600 text-xs font-medium">
                                                        <FiXCircle className="text-xs" />
                                                        Error
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="mt-6">
                            <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                                <span>Progress</span>
                                <span>{Math.round(((Object.values(stageStatus).filter(s => s === 'success').length) / 4) * 100)}%</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-main-blue to-blue-400 rounded-full transition-all duration-500 ease-out"
                                    style={{
                                        width: `${((Object.values(stageStatus).filter(s => s === 'success').length) / 4) * 100}%`
                                    }}
                                ></div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 3: Results */}
                {step === 3 && result && (
                    <div className="animate-fadeIn">
                        {result.status ? (
                            <>
                                {/* Success Header */}
                                <div className="text-center mb-6">
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <FiCheckCircle className="text-green-500 text-3xl" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-main-font">Import Completed!</h3>
                                    <p className="text-gray-500 mt-1">{result.message}</p>
                                </div>

                                {/* Stats Cards */}
                                <div className="grid grid-cols-3 gap-3 mb-6">
                                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                                        <div className="text-2xl font-bold text-green-600">{result.insertedCount}</div>
                                        <div className="text-xs text-green-600 mt-1">Added</div>
                                    </div>
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                                        <div className="text-2xl font-bold text-yellow-600">{result.skippedCount}</div>
                                        <div className="text-xs text-yellow-600 mt-1">Skipped</div>
                                    </div>
                                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                                        <div className="text-2xl font-bold text-red-600">{result.errorCount}</div>
                                        <div className="text-xs text-red-600 mt-1">Errors</div>
                                    </div>
                                </div>

                                {/* Processed Numbers */}
                                {result.processedNumbers && result.processedNumbers.length > 0 && (
                                    <div className="bg-gray-50 rounded-xl p-4 border border-main-border">
                                        <div className="flex items-center gap-2 mb-3">
                                            <FiCheckCircle className="text-green-500" />
                                            <span className="font-medium text-main-font text-sm">Successfully Added Numbers</span>
                                        </div>
                                        <div className="max-h-40 overflow-y-auto">
                                            <div className="flex flex-wrap gap-2">
                                                {result.processedNumbers.map((num, idx) => (
                                                    <span
                                                        key={idx}
                                                        className="inline-flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg text-sm font-mono border border-green-200"
                                                    >
                                                        <FiPhone className="text-green-500 text-xs" />
                                                        {num}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            /* Error State */
                            <div className="text-center py-8">
                                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <FiXCircle className="text-red-500 text-3xl" />
                                </div>
                                <h3 className="text-xl font-semibold text-main-font">Import Failed</h3>
                                <p className="text-gray-500 mt-1">{result.message || 'Something went wrong'}</p>
                            </div>
                        )}

                        {/* Action Button */}
                        <button
                            onClick={handleReset}
                            className="w-full mt-6 py-3 rounded-xl font-medium text-white bg-main-blue hover:bg-main-blue/90 transition-all duration-300 flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-main-blue/25"
                        >
                            <FiRefreshCw />
                            Import More Numbers
                        </button>
                    </div>
                )}
            </div>

            {/* Custom Animation Styles */}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-out;
                }
            `}</style>
        </div>
    );
};

export default MobileAdd;